import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Sozlesme {
    sozlesmeID: number;
    sozlesmeTakipNo: string;
    sozlesmeDurumu: string;
    projeAdi: string | null;
    blokAdi: string | null;
    daireNo: string | null;
}

interface MusteriSatir {
    cariID: number;
    takipNo: string;
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    eklenmeTarihi: string;
    turu2: string | null;
    aktifMi: boolean;
    sozlesmeVar: boolean;
    sozlesmeler: Sozlesme[];
    iysPhone1Onay: boolean;
    iysPhone2Onay: boolean;
    iysEmailOnay: boolean;
    iysOnayVar: boolean;
}

interface Proje {
    projeID: number;
    projeAdi: string;
}

interface Blok {
    blokID: number;
    blokAdi: string;
}

interface SmsSablon {
    sablonID: number;
    sablonAdi: string;
    sablonIcerigi: string;
    standart: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatPhone = (p: string) => {
    const n = p.replace(/\D/g, '');
    if (n.length === 10) return `(${n.slice(0, 3)}) ${n.slice(3, 6)} ${n.slice(6, 8)} ${n.slice(8)}`;
    return p;
};

const formatDate = (s: string) => new Date(s).toLocaleDateString('tr-TR');

// ─── Toplu SMS Modal ─────────────────────────────────────────────────────────

interface TopluSmsModalProps {
    musteriler: MusteriSatir[];
    onClose: () => void;
}

const TopluSmsModal: React.FC<TopluSmsModalProps> = ({ musteriler, onClose }) => {
    const { t } = useTranslation();
    const [mesaj, setMesaj] = useState('');
    const [sablonlar, setSablonlar] = useState<SmsSablon[]>([]);
    const [smsAktif, setSmsAktif] = useState<boolean | null>(null);
    const [gonderiliyor, setGonderiliyor] = useState(false);
    const [sonuclar, setSonuclar] = useState<{ ad: string; telefon: string; basarili: boolean; hata?: string }[]>([]);
    const [tamamlandi, setTamamlandi] = useState(false);

    // Alıcı listesi: aktif telefon bulunan müşteriler
    const alicilar = musteriler.map(m => ({
        cariID: m.cariID,
        ad: m.adiSoyadi,
        telefon: m.telefon1,
        takipNo: m.takipNo,
    })).filter(a => a.telefon);

    useEffect(() => {
        axiosInstance.get('/api/SmsGonder/aktif').then(r => setSmsAktif(r.data.aktif)).catch(() => setSmsAktif(false));
        axiosInstance.get('/api/SmsSablonlari').then(r => {
            // Sadece standart olmayan (kullanıcı tanımlı) şablonlar
            setSablonlar(r.data.filter((s: SmsSablon) => !s.standart));
        }).catch(() => {});
    }, []);

    const handleGonder = async () => {
        if (!mesaj.trim()) { toast.warning(t('msg.uyari')); return; }
        if (alicilar.length === 0) { toast.warning(t('msg.uyari')); return; }
        setGonderiliyor(true);
        const sonucListesi: typeof sonuclar = [];
        for (const alici of alicilar) {
            try {
                const res = await axiosInstance.post('/api/SmsGonder', {
                    telefon: alici.telefon,
                    mesaj,
                    cariID: alici.cariID,
                });
                sonucListesi.push({ ad: alici.ad, telefon: alici.telefon, basarili: res.data?.basarili ?? true });
            } catch (err: any) {
                sonucListesi.push({ ad: alici.ad, telefon: alici.telefon, basarili: false, hata: err?.response?.data || 'Hata' });
            }
        }
        setSonuclar(sonucListesi);
        setTamamlandi(true);
        setGonderiliyor(false);
        const basarili = sonucListesi.filter(s => s.basarili).length;
        toast.success(t('msg.islemBasarili'));
    };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.45)' }}>
            <div style={{ background: '#fff', borderRadius: 14, width: 660, maxWidth: '96vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                {/* Header */}
                <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 }}>{t('sms.topluGonder')}</h2>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '3px 0 0' }}>{alicilar.length} alıcıya gönderilecek</p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>×</button>
                </div>

                <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {!tamamlandi ? (
                        <>
                            {smsAktif === false && (
                                <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400e' }}>
                                    <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>
                                    SMS servisi aktif değil. Tanımlamalar &gt; SMS Ayarları bölümünden aktifleştirin.
                                </div>
                            )}

                            {/* Alıcı listesi */}
                            <div>
                                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>Alıcılar ({alicilar.length})</label>
                                <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f9fafb' }}>
                                    {alicilar.map((a, i) => (
                                        <div key={i} style={{ padding: '7px 12px', borderBottom: i < alicilar.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontWeight: 500, color: '#0f172a' }}>{a.ad}</span>
                                            <span style={{ color: '#64748b', fontSize: 12 }}>{formatPhone(a.telefon)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Şablon seç */}
                            {sablonlar.length > 0 && (
                                <div>
                                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Hazır Şablon Seç</label>
                                    <select
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', color: '#374151' }}
                                        defaultValue=""
                                        onChange={e => {
                                            const s = sablonlar.find(s => String(s.sablonID) === e.target.value);
                                            if (s) setMesaj(s.sablonIcerigi);
                                        }}
                                    >
                                        <option value="">{t('sms.sablonSecin')}</option>
                                        {sablonlar.map(s => (
                                            <option key={s.sablonID} value={s.sablonID}>{s.sablonAdi}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Mesaj */}
                            <div>
                                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                                    Mesaj <span style={{ color: '#94a3b8', fontWeight: 400 }}>({mesaj.length} karakter)</span>
                                </label>
                                <textarea
                                    rows={5}
                                    value={mesaj}
                                    onChange={e => setMesaj(e.target.value)}
                                    placeholder={t('sms.mesajYaz')}
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                />
                            </div>
                        </>
                    ) : (
                        <div>
                            <div style={{ marginBottom: 14, fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                                Gönderim Sonuçları
                            </div>
                            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                {sonuclar.map((s, i) => (
                                    <div key={i} style={{ padding: '8px 12px', borderBottom: i < sonuclar.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <i className={`fas ${s.basarili ? 'fa-check-circle' : 'fa-times-circle'}`} style={{ color: s.basarili ? '#16a34a' : '#dc2626', fontSize: 15 }}></i>
                                        <span style={{ flex: 1, fontSize: 13 }}>{s.ad} — {formatPhone(s.telefon)}</span>
                                        {!s.basarili && <span style={{ fontSize: 11, color: '#ef4444' }}>{s.hata}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button onClick={onClose} style={{ padding: '9px 20px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 500, color: '#374151' }}>
                        {tamamlandi ? 'Kapat' : 'İptal'}
                    </button>
                    {!tamamlandi && (
                        <button
                            onClick={handleGonder}
                            disabled={gonderiliyor || smsAktif === false || !mesaj.trim()}
                            style={{
                                padding: '9px 22px', background: gonderiliyor || smsAktif === false || !mesaj.trim() ? '#94a3b8' : '#cd6906',
                                border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600, color: '#fff',
                                display: 'flex', alignItems: 'center', gap: 7
                            }}
                        >
                            {gonderiliyor ? <><i className="fas fa-spinner fa-spin"></i> Gönderiliyor...</> : <><i className="fas fa-paper-plane"></i> {alicilar.length} kişiye Gönder</>}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const MusteriRaporu: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Filtreler
    const [tarihBas, setTarihBas] = useState('');
    const [tarihBit, setTarihBit] = useState('');
    const [sozlesmeFiltire, setSozlesmeFiltire] = useState('');     // '' / 'var' / 'yok'
    const [projeID, setProjeID] = useState('');
    const [blokID, setBlokID] = useState('');
    const [iysDurumu, setIysDurumu] = useState('');                 // '' / 'onay' / 'ret' / 'yok'
    const [turu2, setTuru2] = useState('');

    // Veriler
    const [satirlar, setSatirlar] = useState<MusteriSatir[]>([]);
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [bloklar, setBloklar] = useState<Blok[]>([]);
    const [loading, setLoading] = useState(false);
    const [arandiMi, setArandiMi] = useState(false);

    // Seçim
    const [seciliIDs, setSeciliIDs] = useState<Set<number>>(new Set());
    const [showSmsModal, setShowSmsModal] = useState(false);

    // Projeler yükle
    useEffect(() => {
        axiosInstance.get('/api/MusteriRaporu/projeler').then(r => setProjeler(r.data)).catch(() => {});
    }, []);

    // Proje değişince blokları yükle
    useEffect(() => {
        if (!projeID) { setBloklar([]); setBlokID(''); return; }
        axiosInstance.get(`/api/MusteriRaporu/bloklar/${projeID}`).then(r => setBloklar(r.data)).catch(() => {});
        setBlokID('');
    }, [projeID]);

    const handleAra = useCallback(async () => {
        setLoading(true);
        setArandiMi(true);
        setSeciliIDs(new Set());
        try {
            const params: Record<string, string> = {};
            if (tarihBas) params.eklenmeTarihiBaslangic = tarihBas;
            if (tarihBit) params.eklenmeTarihiBitis = tarihBit;
            if (sozlesmeFiltire) params.sozlesmeDurumu = sozlesmeFiltire;
            if (projeID) params.projeID = projeID;
            if (blokID) params.blokID = blokID;
            if (iysDurumu) params.iysDurumu = iysDurumu;
            if (turu2) params.turu2 = turu2;
            const res = await axiosInstance.get('/api/MusteriRaporu', { params });
            setSatirlar(res.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setLoading(false);
        }
    }, [tarihBas, tarihBit, sozlesmeFiltire, projeID, blokID, iysDurumu, turu2]);

    const handleTemizle = () => {
        setTarihBas(''); setTarihBit('');
        setSozlesmeFiltire(''); setProjeID(''); setBlokID('');
        setIysDurumu(''); setTuru2('');
        setSatirlar([]); setArandiMi(false); setSeciliIDs(new Set());
    };

    // Seçim işlemleri
    const toggleSecim = (id: number) => {
        setSeciliIDs(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const tumunuSec = (sec: boolean) => {
        if (sec) setSeciliIDs(new Set(satirlar.map(s => s.cariID)));
        else setSeciliIDs(new Set());
    };

    const seciliMusteriler = satirlar.filter(s => seciliIDs.has(s.cariID));

    // Excel export
    const handleExcelExport = () => {
        if (satirlar.length === 0) { toast.warning(t('msg.uyari')); return; }
        const data = satirlar.map(s => ({
            'Takip No': s.takipNo,
            'Ad Soyad': s.adiSoyadi,
            'Firma Adı': s.firmaAdi ?? '',
            'Telefon 1': s.telefon1,
            'Telefon 2': s.telefon2 ?? '',
            'E-Posta': s.ePosta ?? '',
            'Alt Kategori': s.turu2 ?? '',
            'Eklenme Tarihi': formatDate(s.eklenmeTarihi),
            'Sözleşme': s.sozlesmeVar ? 'Var' : 'Yok',
            'Projeler': s.sozlesmeler.map(sz => sz.projeAdi).filter(Boolean).join(', '),
            'IYS Onay': s.iysOnayVar ? 'Var' : 'Yok',
            'Durum': s.aktifMi ? 'Aktif' : 'Pasif',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Müşteri Raporu');
        XLSX.writeFile(wb, `musteri_raporu_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success(t('msg.islemBasarili'));
    };

    // ─── Render ──────────────────────────────────────────────────────────────

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '8px 11px', border: '1px solid #d1d5db',
        borderRadius: 7, fontSize: 13, outline: 'none', background: '#fff', color: '#374151', boxSizing: 'border-box'
    };
    const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 };

    return (
        <div style={{ padding: '24px 28px' }}>
            {/* Başlık */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={() => navigate('/rapor-merkezi')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 20, padding: 0 }}>
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>{t('rapor.musteriRaporu')}</h1>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '3px 0 0' }}>{t('musteriRaporu.aciklama', 'Filtrelerle müşteri kayıtlarını sorgulayın ve toplu işlem yapın.')}</p>
                    </div>
                </div>
                <button
                    onClick={handleExcelExport}
                    style={{ padding: '9px 18px', background: '#16a34a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}
                >
                    <i className="fas fa-file-excel"></i> {t('btn.excelAktar', 'Excel Aktar')}
                </button>
            </div>

            {/* Filtre Kartı */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.05)' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' }}>
                    <i className="fas fa-filter" style={{ marginRight: 7, color: '#cd6906' }}></i>{t('label.filtreler', 'Filtreler')}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px 20px' }}>
                    {/* Eklenme Tarihi Başlangıç */}
                    <div>
                        <label style={labelStyle}>{t('label.baslangicTarihi', 'Eklenme Tarihi (Başlangıç)')}</label>
                        <input type="date" value={tarihBas} onChange={e => setTarihBas(e.target.value)} style={inputStyle} />
                    </div>
                    {/* Eklenme Tarihi Bitiş */}
                    <div>
                        <label style={labelStyle}>{t('label.bitisTarihi', 'Eklenme Tarihi (Bitiş)')}</label>
                        <input type="date" value={tarihBit} onChange={e => setTarihBit(e.target.value)} style={inputStyle} />
                    </div>
                    {/* Sözleşme Durumu */}
                    <div>
                        <label style={labelStyle}>{t('label.sozlesme', 'Sözleşme')}</label>
                        <select value={sozlesmeFiltire} onChange={e => setSozlesmeFiltire(e.target.value)} style={inputStyle}>
                            <option value="">{t('label.tumu')}</option>
                            <option value="var">{t('label.sozlesmesiVar', 'Sözleşmesi Var')}</option>
                            <option value="yok">{t('label.sozlesmesiYok', 'Sözleşmesi Yok')}</option>
                        </select>
                    </div>
                    {/* Proje */}
                    <div>
                        <label style={labelStyle}>{t('label.proje', 'Proje')}</label>
                        <select value={projeID} onChange={e => setProjeID(e.target.value)} style={inputStyle}>
                            <option value="">{t('label.tumProjeler', 'Tüm Projeler')}</option>
                            {projeler.map(p => (
                                <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>
                            ))}
                        </select>
                    </div>
                    {/* Blok */}
                    <div>
                        <label style={labelStyle}>{t('label.blok', 'Blok')}</label>
                        <select value={blokID} onChange={e => setBlokID(e.target.value)} style={inputStyle} disabled={!projeID}>
                            <option value="">{t('label.tumBloklar', 'Tüm Bloklar')}</option>
                            {bloklar.map(b => (
                                <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>
                            ))}
                        </select>
                    </div>
                    {/* IYS Durumu */}
                    <div>
                        <label style={labelStyle}>{t('label.iysOnayDurumu', 'IYS Onay Durumu')}</label>
                        <select value={iysDurumu} onChange={e => setIysDurumu(e.target.value)} style={inputStyle}>
                            <option value="">{t('label.tumu')}</option>
                            <option value="onay">{t('label.onayAlinmis', 'Onay Alınmış')}</option>
                            <option value="ret">{t('label.retVerilmis', 'Ret Verilmiş')}</option>
                            <option value="yok">{t('label.izinKaydiYok', 'İzin Kaydı Yok')}</option>
                        </select>
                    </div>
                    {/* Alt Kategori (Tür2) */}
                    <div>
                        <label style={labelStyle}>{t('label.altKategori', 'Alt Kategori')}</label>
                        <input type="text" value={turu2} onChange={e => setTuru2(e.target.value)} placeholder={t('label.altKategori', 'Alt Kategori')} style={inputStyle} />
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
                    <button onClick={handleTemizle} style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#374151' }}>
                        <i className="fas fa-times" style={{ marginRight: 6 }}></i>{t('btn.temizle', 'Temizle')}
                    </button>
                    <button
                        onClick={handleAra}
                        disabled={loading}
                        style={{ padding: '9px 22px', background: '#cd6906', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: 7 }}
                    >
                        {loading ? <><i className="fas fa-spinner fa-spin"></i> {t('msg.yukleniyor', 'Yükleniyor...')}</> : <><i className="fas fa-search"></i> {t('btn.listele', 'Listele')}</>}
                    </button>
                </div>
            </div>

            {/* Sonuç Tablosu */}
            {arandiMi && (
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(15,23,42,0.05)', overflow: 'hidden' }}>
                    {/* Tablo header */}
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                            {loading ? t('msg.yukleniyor') : `${satirlar.length} kayıt bulundu`}
                            {seciliIDs.size > 0 && (
                                <span style={{ marginLeft: 10, fontSize: 12, color: '#cd6906', fontWeight: 500 }}>({seciliIDs.size} seçili)</span>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {satirlar.length > 0 && (
                                <button
                                    onClick={() => tumunuSec(seciliIDs.size < satirlar.length)}
                                    style={{ padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, cursor: 'pointer', fontWeight: 500, color: '#374151' }}
                                >
                                    {seciliIDs.size < satirlar.length ? t('btn.tumunuSec', 'Tümünü Seç') : t('btn.secimiKaldir', 'Seçimi Kaldır')}
                                </button>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                            <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i>
                        </div>
                    ) : satirlar.length === 0 ? (
                        <div style={{ padding: '48px 0', textAlign: 'center' }}>
                            <i className="fas fa-users" style={{ fontSize: 32, color: '#e2e8f0', display: 'block', marginBottom: 12 }}></i>
                            <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>{t('msg.filtreUygunKayitYok', 'Filtrelere uygun kayıt bulunamadı.')}</p>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        <th style={{ width: 42, padding: '10px 14px', textAlign: 'center' }}>
                                            <input type="checkbox"
                                                checked={seciliIDs.size === satirlar.length && satirlar.length > 0}
                                                onChange={e => tumunuSec(e.target.checked)}
                                                style={{ cursor: 'pointer', width: 15, height: 15 }}
                                            />
                                        </th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>{t('label.adSoyad')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>{t('label.telefon')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>{t('label.altKategori', 'Alt Kategori')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>{t('label.eklemeTarihi', 'Eklenme Tarihi')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>{t('label.sozlesme', 'Sözleşme')}</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>IYS</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>{t('label.islem')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {satirlar.map((s, idx) => (
                                        <tr key={s.cariID} style={{ borderBottom: '1px solid #f1f5f9', background: seciliIDs.has(s.cariID) ? '#fff8f0' : (idx % 2 === 0 ? '#fff' : '#fafafa') }}>
                                            {/* Checkbox */}
                                            <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                <input type="checkbox"
                                                    checked={seciliIDs.has(s.cariID)}
                                                    onChange={() => toggleSecim(s.cariID)}
                                                    style={{ cursor: 'pointer', width: 15, height: 15 }}
                                                />
                                            </td>
                                            {/* Ad Soyad */}
                                            <td style={{ padding: '10px 14px' }}>
                                                <div style={{ fontWeight: 600, color: '#0f172a' }}>{s.adiSoyadi}</div>
                                                {s.firmaAdi && <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.firmaAdi}</div>}
                                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.takipNo}</div>
                                            </td>
                                            {/* Telefon */}
                                            <td style={{ padding: '10px 14px' }}>
                                                <div style={{ fontFamily: 'monospace', color: '#374151', fontSize: 12 }}>{formatPhone(s.telefon1)}</div>
                                                {s.telefon2 && <div style={{ fontFamily: 'monospace', color: '#94a3b8', fontSize: 11 }}>{formatPhone(s.telefon2)}</div>}
                                            </td>
                                            {/* Alt Kategori */}
                                            <td style={{ padding: '10px 14px', color: s.turu2 ? '#374151' : '#cbd5e1' }}>
                                                {s.turu2 || '—'}
                                            </td>
                                            {/* Eklenme Tarihi */}
                                            <td style={{ padding: '10px 14px', color: '#374151', whiteSpace: 'nowrap' }}>
                                                {formatDate(s.eklenmeTarihi)}
                                            </td>
                                            {/* Sözleşme */}
                                            <td style={{ padding: '10px 14px' }}>
                                                {s.sozlesmeVar ? (
                                                    <div>
                                                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>{t('label.var', 'Var')}</span>
                                                        <div style={{ marginTop: 4 }}>
                                                            {s.sozlesmeler.slice(0, 2).map(sz => (
                                                                <div key={sz.sozlesmeID} style={{ fontSize: 11, color: '#64748b' }}>
                                                                    {sz.projeAdi} {sz.blokAdi ? `- ${sz.blokAdi}` : ''} {sz.daireNo ? `(${sz.daireNo})` : ''}
                                                                </div>
                                                            ))}
                                                            {s.sozlesmeler.length > 2 && <div style={{ fontSize: 11, color: '#94a3b8' }}>+{s.sozlesmeler.length - 2} daha</div>}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: '#fee2e2', color: '#991b1b', fontWeight: 600 }}>{t('label.yok', 'Yok')}</span>
                                                )}
                                            </td>
                                            {/* IYS */}
                                            <td style={{ padding: '10px 14px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <span style={{ fontSize: 11, color: s.iysPhone1Onay ? '#16a34a' : '#94a3b8' }}>
                                                        <i className={`fas ${s.iysPhone1Onay ? 'fa-check-circle' : 'fa-times-circle'}`} style={{ marginRight: 4 }}></i>Tel1
                                                    </span>
                                                    {s.telefon2 && (
                                                        <span style={{ fontSize: 11, color: s.iysPhone2Onay ? '#16a34a' : '#94a3b8' }}>
                                                            <i className={`fas ${s.iysPhone2Onay ? 'fa-check-circle' : 'fa-times-circle'}`} style={{ marginRight: 4 }}></i>Tel2
                                                        </span>
                                                    )}
                                                    {s.ePosta && (
                                                        <span style={{ fontSize: 11, color: s.iysEmailOnay ? '#16a34a' : '#94a3b8' }}>
                                                            <i className={`fas ${s.iysEmailOnay ? 'fa-check-circle' : 'fa-times-circle'}`} style={{ marginRight: 4 }}></i>E-posta
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            {/* İşlemler */}
                                            <td style={{ padding: '10px 14px' }}>
                                                <button
                                                    onClick={() => navigate(`/musteriler/${s.cariID}`)}
                                                    style={{ padding: '5px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#374151', fontWeight: 500 }}
                                                >
                                                    <i className="fas fa-eye" style={{ marginRight: 5 }}></i>{t('btn.detaylar', 'Detay')}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Toplu İşlem Çubuğu */}
            {seciliIDs.size > 0 && (
                <div style={{
                    position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                    background: '#1e293b', borderRadius: 12, padding: '14px 24px',
                    display: 'flex', alignItems: 'center', gap: 16,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)', zIndex: 100
                }}>
                    <span style={{ fontSize: 14, color: '#f1f5f9', fontWeight: 500 }}>
                        <i className="fas fa-check-square" style={{ marginRight: 8, color: '#cd6906' }}></i>
                        {seciliIDs.size} {t('label.musteriSecildi', 'müşteri seçildi')}
                    </span>
                    <div style={{ width: 1, height: 20, background: '#334155' }}></div>
                    <button
                        onClick={() => setShowSmsModal(true)}
                        style={{ padding: '8px 18px', background: '#cd6906', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}
                    >
                        <i className="fas fa-sms"></i> {t('sms.topluGonder', 'Toplu SMS Gönder')}
                    </button>
                    <button
                        onClick={() => setSeciliIDs(new Set())}
                        style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13, padding: '4px 8px' }}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            )}

            {/* Toplu SMS Modal */}
            {showSmsModal && (
                <TopluSmsModal
                    musteriler={seciliMusteriler}
                    onClose={() => setShowSmsModal(false)}
                />
            )}
        </div>
    );
};

export default MusteriRaporu;
